// Estado del banner global «Estamos actualizando SmartDx» (#1757 v2, opción C del mockup).
// Spec: .claude/specs/1757-login-mantenimiento/ (R5, R7, R8, R12).
//
// Un solo dueño del estado; `components/BannerMantenimiento.jsx` solo lo dibuja. Entra por dos
// eventos de `window` que emite `get-client.js` (no al revés: get-client no importa este módulo):
//   · `smartdx:mantenimiento`: una llamada terminó en 502/503 legible (o la sonda de arranque).
//   · `smartdx:backend-ok`: una llamada terminó en 2xx.
//
// Estados: oculto -> (señal, 1,5 s, sonda en mantenimiento) -> espera <-> probando -> volvio -> oculto.
//
// Umbral anti-parpadeo (R5): la señal NO muestra el banner por sí sola. Arma una espera de 1,5 s y
// sondea UNA vez `GET /api/user/csrf/` same-origin; solo si la sonda también responde 502/503 el
// banner sale. Un 502 aislado (la sonda responde bien) no muestra nada, y una ráfaga de llamadas
// fallando a la vez comparte la misma espera (una sola sonda). Se descartó contar errores: el login
// hace UNA llamada y es la que más importa.
//
// La sonda es un GET sin clave, sin credenciales y sin datos, así que sondear cada 15 s sin tope es
// inocuo (la v1 tenía tope de 5 min porque reenviaba el POST de login con la clave).
// Este módulo NO conoce el login: la vuelta jamás ingresa, navega ni toca un formulario (R9).
import { esMantenimientoRespuesta } from "./get-client";

export const CONFIRMAR_MS = 1500; // persistencia antes de la sonda de confirmación
export const TIC_MS = 15000; // entre sondas mientras el banner está visible (el RETRY del mockup)
export const VOLVIO_MS = 4000; // cuánto dura el verde antes de irse solo
export const SONDA_TIMEOUT_MS = 5000; // techo de cada sonda
const MIN_ENTRE_SONDAS_MS = 2000; // para que la señal de vida (R12) no ametralle
const ENFRIAR_MS = 10000; // tras un 502 aislado, las señales siguientes no re-sondean (un endpoint roto no ametralla)

const RUTA_SONDA = "/api/user/csrf/";

const INICIAL = Object.freeze({ estado: "oculto", proximoEn: null });
let foto = INICIAL;
const oyentes = new Set();

let temporizadorConfirmar = null;
let temporizadorTic = null;
let temporizadorVolvio = null;
let enVuelo = false;
let ultimaSondaEn = 0;
let sinConfirmarHasta = 0; // fin del enfriamiento tras una confirmación negativa
let esperandoConfirmacion = []; // resolvers de confirmarMantenimiento()

const publicar = (estado, proximoEn = null) => {
  foto = Object.freeze({ estado, proximoEn });
  oyentes.forEach((fn) => {
    try {
      fn();
    } catch {
      /* un oyente roto no tumba a los demás */
    }
  });
};

export const getEstadoMantenimiento = () => foto;
export const suscribirMantenimiento = (fn) => {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
};

const resolverConfirmaciones = (valor) => {
  const lista = esperandoConfirmacion;
  esperandoConfirmacion = [];
  lista.forEach((r) => r(valor));
};

// "mantenimiento" (502/503 no-JSON) | "ok" (2xx) | "otro" (500, 504, 503 JSON, red caída, timeout).
// Nunca rechaza. Solo "ok" declara la vuelta: con cualquier otra cosa el banner sigue (R7).
const sondear = async () => {
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), SONDA_TIMEOUT_MS) : null;
  try {
    const r = await fetch(RUTA_SONDA, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal: ctrl ? ctrl.signal : undefined,
    });
    if (esMantenimientoRespuesta(r.status, r.headers?.get?.("content-type"))) return "mantenimiento";
    return r.ok ? "ok" : "otro";
  } catch {
    return "otro";
  } finally {
    if (t) clearTimeout(t);
  }
};

const limpiar = (t) => {
  if (t) clearTimeout(t);
  return null;
};

const programarTic = () => {
  temporizadorTic = limpiar(temporizadorTic);
  const proximoEn = Date.now() + TIC_MS;
  publicar("espera", proximoEn);
  temporizadorTic = setTimeout(() => {
    temporizadorTic = null;
    probar();
  }, TIC_MS);
};

const irAVolvio = () => {
  temporizadorTic = limpiar(temporizadorTic);
  temporizadorVolvio = limpiar(temporizadorVolvio);
  publicar("volvio", null);
  temporizadorVolvio = setTimeout(() => {
    temporizadorVolvio = null;
    publicar("oculto", null);
  }, VOLVIO_MS);
};

// Sonda del banner visible (espera/probando). Una sola en vuelo.
const probar = async () => {
  if (enVuelo || foto.estado === "oculto" || foto.estado === "volvio") return;
  enVuelo = true;
  ultimaSondaEn = Date.now();
  temporizadorTic = limpiar(temporizadorTic);
  publicar("probando", null);
  let r;
  try {
    r = await sondear();
  } finally {
    enVuelo = false;
  }
  if (foto.estado !== "probando") return; // reiniciado o retirado mientras la sonda iba
  if (r === "ok") irAVolvio();
  else programarTic();
};

const alSenalMantenimiento = () => {
  // Una señal nueva durante el verde lo retira y vuelve a confirmar (R8).
  if (foto.estado === "volvio") {
    temporizadorVolvio = limpiar(temporizadorVolvio);
    publicar("oculto", null);
  }
  if (foto.estado !== "oculto" || temporizadorConfirmar || enVuelo) return;
  if (Date.now() < sinConfirmarHasta) return;
  temporizadorConfirmar = setTimeout(async () => {
    temporizadorConfirmar = null;
    if (foto.estado !== "oculto" || enVuelo) return;
    enVuelo = true;
    ultimaSondaEn = Date.now();
    let r;
    try {
      r = await sondear();
    } finally {
      enVuelo = false;
    }
    if (r === "mantenimiento" && foto.estado === "oculto") {
      programarTic();
      resolverConfirmaciones(true);
    } else {
      sinConfirmarHasta = Date.now() + ENFRIAR_MS;
      resolverConfirmaciones(foto.estado !== "oculto");
    }
  }, CONFIRMAR_MS);
};

// R12: un 2xx de otra llamada adelanta la sonda; la sonda decide si hay verde.
const alBackendOk = () => {
  if (foto.estado !== "espera") return;
  if (Date.now() - ultimaSondaEn < MIN_ENTRE_SONDAS_MS) return;
  probar();
};

// «Intentar ahora»: sonda en este momento; la cuenta de 15 s se reinicia al terminar.
export const reintentarAhora = () => {
  if (foto.estado === "espera") probar();
};

// Para el login (R18): ¿el banner está (o está por estar) explicando el problema?
//  · visible: true de una.
//  · hay una confirmación en curso: espera su resultado.
//  · nada en curso: false (el 502 fue aislado y el login muestra su error de siempre).
export const confirmarMantenimiento = () => {
  if (foto.estado === "espera" || foto.estado === "probando") return Promise.resolve(true);
  if (!temporizadorConfirmar && !enVuelo) return Promise.resolve(false);
  return new Promise((resolve) => {
    esperandoConfirmacion.push(resolve);
  });
};

// Solo para tests: deja el módulo como recién cargado.
export const __reiniciarMantenimiento = () => {
  temporizadorConfirmar = limpiar(temporizadorConfirmar);
  temporizadorTic = limpiar(temporizadorTic);
  temporizadorVolvio = limpiar(temporizadorVolvio);
  enVuelo = false;
  ultimaSondaEn = 0;
  sinConfirmarHasta = 0;
  resolverConfirmaciones(false);
  foto = INICIAL;
  oyentes.forEach((fn) => {
    try {
      fn();
    } catch {
      /* idem */
    }
  });
};

if (typeof window !== "undefined") {
  window.addEventListener("smartdx:mantenimiento", alSenalMantenimiento);
  window.addEventListener("smartdx:backend-ok", alBackendOk);
}
